import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Clock, Infinity as InfinityIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import RequestMoreTimeModal from './RequestMoreTimeModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AutoUsageMeter — the dashboard-facing meter that renders directly
 * below the "Start Session" CTA. Beta testers see:
 *   · how many Auto Conversation minutes they've burned this period
 *   · how many remain
 *   · when the period resets
 *   · a soft warning once they cross 80% utilisation
 *   · a hard "limit reached" state once they're at/past 100%, with a
 *     one-tap link to request more time (15 / 30 / 60 min blocks)
 *
 * Source of truth: GET /api/usage/auto. We also expose a parent-driven
 * `refreshKey` prop so the dashboard (or the end of an Auto session)
 * can force a re-fetch without hard-reloading the page.
 */
const formatMinutes = (m) => {
  if (m == null) return '—';
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rem = Math.round(m - h * 60);
    return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
  }
  return `${Math.round(m)}m`;
};

const formatResetDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (_) {
    return '—';
  }
};

const AutoUsageMeter = ({ refreshKey = 0 }) => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);

  const fetchUsage = useCallback(async () => {
    const token = localStorage.getItem('sage_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/usage/auto`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsage(data);
    } catch (err) {
      // Non-fatal: an unreachable backend shouldn't block the dashboard.
      if (process.env.NODE_ENV !== 'production') console.debug('Usage fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage, refreshKey]);

  if (loading) {
    return (
      <div
        data-testid="auto-usage-meter-loading"
        className="mt-4 flex items-center justify-center text-xs text-zinc-500 gap-2"
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        Loading usage…
      </div>
    );
  }

  if (!usage) return null;

  const pct = usage.minutes_limit > 0
    ? Math.min(100, (usage.minutes_used / usage.minutes_limit) * 100)
    : 0;

  // Three visual states — the colour language is intentionally subtle.
  // Comfortable zone: cream/amber. Warning (>=80%): warmer amber.
  // Over limit: a muted ember without going full-red, so the UI stays
  // calm and doesn't shame the user.
  let barClass = 'bg-amber-200/40';
  let labelClass = 'text-zinc-400';
  let state = 'ok';
  if (usage.over_limit) {
    barClass = 'bg-amber-400/70';
    labelClass = 'text-amber-200';
    state = 'over';
  } else if (pct >= 80) {
    barClass = 'bg-amber-300/60';
    labelClass = 'text-amber-100/90';
    state = 'warning';
  }

  const resetLabel = formatResetDate(usage.period_end);
  const requestingDisabled = hasPendingRequest || requestModalOpen;

  return (
    <>
      <div
        data-testid="auto-usage-meter"
        data-state={state}
        className="mt-5 max-w-md mx-auto text-left"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-500">
            <Clock className="w-3 h-3" />
            <span>Auto minutes this period</span>
          </div>
          <span
            className={`text-xs font-mono ${labelClass}`}
            data-testid="auto-usage-remaining"
          >
            {formatMinutes(usage.minutes_remaining)} left
          </span>
        </div>

        {/* Progress rail — the meter itself. Width is clamped to 100
            so visual honesty is preserved when a user is OVER limit. */}
        <div
          className="w-full h-1.5 rounded-full bg-white/5 overflow-hidden"
          role="progressbar"
          aria-valuenow={Math.round(pct)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Auto Conversation monthly usage"
        >
          <div
            className={`h-full ${barClass} transition-all duration-500`}
            style={{ width: `${pct}%` }}
          />
        </div>

        <div className="mt-2 flex items-center justify-between text-[11px] text-zinc-500">
          <span data-testid="auto-usage-used">
            {formatMinutes(usage.minutes_used)} of {formatMinutes(usage.minutes_limit)} used
          </span>
          <span data-testid="auto-usage-resets">
            Resets {resetLabel}
          </span>
        </div>

        {/* Contextual secondary line below the rail — only shown in the
            interesting states so the common case stays clean. */}
        {usage.over_limit && usage.grace_used && (
          <p
            data-testid="auto-usage-hard-block"
            className="mt-3 text-xs text-amber-200/90 leading-relaxed"
          >
            You've reached your monthly limit. Hold-to-Talk is still open,
            and you can request more Auto time below.
          </p>
        )}
        {usage.over_limit && !usage.grace_used && (
          <p
            data-testid="auto-usage-grace-available"
            className="mt-3 text-xs text-amber-100/80 leading-relaxed"
          >
            You're a hair over your monthly budget — one grace session is
            still available before Auto pauses until the next reset.
          </p>
        )}

        {/* Request-more CTA — visible once the user has consumed ≥80%
            or already crossed into the over-limit zone, where it's
            actually useful. Hidden otherwise to keep the hero quiet. */}
        {(state === 'warning' || state === 'over') && (
          <button
            type="button"
            onClick={() => setRequestModalOpen(true)}
            disabled={requestingDisabled}
            data-testid="request-more-time-button"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-amber-200/80 hover:text-amber-100 transition-colors disabled:opacity-60"
          >
            <InfinityIcon className="w-3 h-3" />
            {hasPendingRequest ? 'Request pending…' : 'Request more time'}
          </button>
        )}

        {usage.minutes_extra_granted > 0 && (
          <p
            data-testid="auto-usage-extra-granted"
            className="mt-2 text-[11px] text-emerald-300/80"
          >
            +{usage.minutes_extra_granted} minutes added this period
          </p>
        )}
      </div>

      <RequestMoreTimeModal
        open={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
        onSuccess={(status) => {
          if (status === 'existing') {
            toast.info("You've already got a request pending — we'll let you know soon.");
          } else {
            toast.success('Request sent. We\'ll be in touch shortly.');
          }
          setHasPendingRequest(true);
          setRequestModalOpen(false);
          fetchUsage();
        }}
      />
    </>
  );
};

export default AutoUsageMeter;
